/**
 * Move dialog (Block 3.3). A minimal folder picker: browse the workspace tree
 * from the root and drop the target into the chosen folder. The server re-checks
 * write on source + destination and rejects folder cycles, so this UI is only a
 * convenience.
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";

import "./ryfe.scss";

import type { FolderChild, FolderListing } from "../api";

interface Target {
  kind: "folder" | "file";
  id: string;
  name: string;
}

export const MoveDialog = ({
  wsId,
  target,
  onClose,
  onMoved,
}: {
  wsId: string;
  target: Target;
  onClose: () => void;
  onMoved: () => void;
}) => {
  const [listing, setListing] = useState<FolderListing | null>(null);
  const [stack, setStack] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const openRoot = useCallback(async () => {
    const root = await ryfeApi.getRoot(wsId);
    setListing(root);
    setStack([{ id: root.id, name: "Home" }]);
  }, [wsId]);

  useEffect(() => {
    openRoot().catch((e) => setError((e as Error).message));
  }, [openRoot]);

  const enter = async (f: FolderChild) => {
    try {
      const l = await ryfeApi.listFolder(wsId, f.id);
      setListing(l);
      setStack((s) => [...s, { id: f.id, name: f.name }]);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const up = async (depth: number) => {
    const dest = stack[depth];
    try {
      const l =
        depth === 0
          ? await ryfeApi.getRoot(wsId)
          : await ryfeApi.listFolder(wsId, dest.id);
      setListing(l);
      setStack((s) => s.slice(0, depth + 1));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const current = stack[stack.length - 1];
  const isSelf = target.kind === "folder" && current?.id === target.id;

  const moveHere = async () => {
    if (!current || isSelf) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (target.kind === "file") {
        await ryfeApi.patchFile(wsId, target.id, { folderId: current.id });
      } else {
        await ryfeApi.patchFolder(wsId, target.id, { parentId: current.id });
      }
      onMoved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Move failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">„{target.name}“ verschieben nach…</h2>

        <nav className="ryfe-breadcrumb">
          {stack.map((c, i) => (
            <span key={c.id}>
              {i > 0 && " / "}
              <button className="ryfe-btn ryfe-btn--link" onClick={() => up(i)}>
                {c.name}
              </button>
            </span>
          ))}
        </nav>

        <div className="ryfe-picker">
          {!listing ? (
            <div className="ryfe-muted">Loading…</div>
          ) : listing.folders.filter((f) => f.id !== target.id).length === 0 ? (
            <div className="ryfe-muted">Keine Unterordner.</div>
          ) : (
            listing.folders
              .filter((f) => f.id !== target.id)
              .map((f) => (
                <button
                  key={f.id}
                  className="ryfe-picker__row"
                  onClick={() => enter(f)}
                >
                  📁 {f.name}
                </button>
              ))
          )}
        </div>

        {error && (
          <div className="ryfe-auth__error" role="alert">
            {error}
          </div>
        )}

        <div className="ryfe-modal__actions">
          <span className="ryfe-topbar__spacer" />
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="ryfe-btn ryfe-btn--primary"
            onClick={moveHere}
            disabled={busy || isSelf}
            title={isSelf ? "Ein Ordner kann nicht in sich selbst" : ""}
          >
            Hierher verschieben
          </button>
        </div>
      </div>
    </div>
  );
};
